/**
 * Values of the `Device Category` analytics super-property, shared with iOS
 * (`GLDevice.deviceType`) and Android (`DeviceCategory`) — keep them in sync so one Mixpanel
 * report can compare an iPad against an Android tablet. `Phablet` is the iPhone Duo: iPad-like
 * unfolded, but neither an iPhone nor an iPad.
 */
export type DeviceCategory = 'Tablet' | 'Mobile' | 'Desktop' | 'Phablet'

/**
 * Parses the `client-device-category` request header. Unlike `client-platform`, whose unrecognized
 * values raise a forced-update 401, an unrecognized category must never fail the request — clients
 * ship new values before servers know them.
 */
export function deviceCategoryFromString(value: string): DeviceCategory | undefined {
  switch (value.toLowerCase()) {
    case 'tablet':
      return 'Tablet'
    case 'mobile':
      return 'Mobile'
    case 'desktop':
      return 'Desktop'
    case 'phablet':
      return 'Phablet'
  }

  return undefined
}

/**
 * True for a user agent + touch-point pair matching iPadOS 13+'s "look like a Mac" spoof. Shared
 * with `detectOperatingSystem` in common-frontend's `platform.ts`, which needs the same signal for
 * its `iPadOS` case — keep the two in sync.
 *
 * Matches `Macintosh` and not the looser `Mac`: iPhone and pre-13 iPad agents both say "like Mac OS
 * X", and every one of them reports touch points, so `Mac` would call them tablets too.
 */
export function isModernIPadUserAgent(userAgent: string, maxTouchPoints: number): boolean {
  return /Macintosh/.test(userAgent) && maxTouchPoints > 1
}

/**
 * The form factor a browser is running on, from the values `navigator` exposes.
 *
 * Tablet is checked first because a pre-iPadOS-13 iPad and an Android tablet both carry markers
 * the mobile check would otherwise claim. `Desktop` is the fallback, so a browser with a spoofed
 * or stripped user agent lands there rather than in a tablet count someone is sizing a market on.
 */
export function deviceCategoryFor(userAgent: string, maxTouchPoints: number): DeviceCategory {
  // Android's own convention: its tablet browsers drop the `Mobile` token its phones carry. That
  // makes the absence of the token the whole test, so an Android tablet that keeps it — Silk's
  // mobile view, or "request mobile site" — is counted `Mobile`. Known floor, not a fixable one
  // from the user agent alone.
  const isAndroidTablet = /Android/.test(userAgent) && !/Mobile/.test(userAgent)

  if (/Tablet|iPad/.test(userAgent) || isAndroidTablet || isModernIPadUserAgent(userAgent, maxTouchPoints)) {
    return 'Tablet'
  }
  if (/Mobile|iPhone|iPod/.test(userAgent)) {
    return 'Mobile'
  }
  return 'Desktop'
}
